/**
 * CR-7418: the OTIF decision vocabulary, in one place.
 *
 * A line is Not Decided, Overridden or Acknowledged, or it has no review to
 * report at all. Every surface that prints that answer calls this: the PO
 * Lines grid column and its row actions, the line detail modal, the OTIF and
 * OTD drill-downs, the Supplier Performance PO-line drill and the Appeals
 * grid. The grid needed a badge and the obvious move was to copy the six lines
 * that already existed; a second implementation of the same predicate is not a
 * duplicate to keep in step, it is the next round's finding.
 *
 * The values are the server's and the strings are what the `otif_decision`
 * query param carries, so a name here is the same name in the URL, the API and
 * the digest email's deep link.
 */
import { Badge } from '../ui/badge';
import type { PurchaseOrderLine } from '../../lib/api';
import type { ChangelogEntry } from '../common/Changelog';

export type OtifDecisionFilter = 'all' | 'not_decided' | 'overridden' | 'acknowledged';
export type OtifDecision = Exclude<OtifDecisionFilter, 'all'>;

export const OTIF_DECISION_FILTERS: readonly OtifDecisionFilter[] = [
  'all', 'not_decided', 'overridden', 'acknowledged',
] as const;

/** Whether a URL-supplied value is one of the decision filters (CR-6519). */
export function isOtifDecisionFilter(value: string | null): value is OtifDecisionFilter {
  return value !== null && (OTIF_DECISION_FILTERS as readonly string[]).includes(value);
}

/** The shape any surface needs before it can state a review status. */
export type OtifReviewable = Pick<PurchaseOrderLine,
  'otif_overridden' | 'otif_acknowledged' | 'status' | 'quantity' | 'received_qty' |
  'is_otif' | 'acknowledge_status' | 'previous_acknowledge_status' | 'declined_supplier_change'>;

/**
 * Does this line OWE a decision? The mirror of `not_decided()` /
 * `_awaits_decision` in `purchasing/services/otif_review.py`, clause for
 * clause and in the same order: delivered in full, still failing, and not a
 * procurement-declined supplier change.
 *
 * "Not Decided" claims somebody has work to do, so it may only be printed
 * where a decision is possible. Without this the column showed an amber
 * "waiting on a person" chip against every pending line the moment a user
 * turned it on from a tab other than the review pill.
 *
 * KEEP IN STEP WITH THE SERVER. This is the one predicate that could not be
 * collapsed into the Python definition, because the PO Lines grid derives the
 * status client-side from the flags the list payload already carries rather
 * than asking for a seventh serializer field. `otifDecision.test.tsx` pins the
 * cases; the Python twin is pinned by `test_cr7418_otif_review_status.py`.
 */
export function otifAwaitsDecision(line: Partial<OtifReviewable>): boolean {
  if (line.status !== 'received') return false;
  const ordered = Number(line.quantity ?? 0);
  const received = Number(line.received_qty ?? 0);
  if (!(ordered > 0) || received < ordered) return false;
  // Still failing: a line that passed needs no ruling, and one not yet judged
  // (`is_otif` null) has nothing to rule on yet.
  if (line.is_otif !== false) return false;
  // The server's is_declined_supplier_change: its own column (EXPEDITING-3 D2),
  // or the status pair for rows written before it existed.
  if (line.declined_supplier_change) return false;
  if (line.acknowledge_status === 'change_declined'
      && line.previous_acknowledge_status === 'change_proposed_supplier') return false;
  return true;
}

/** The decision a line currently carries, or `undefined` where it has no
 *  review to report. Overridden wins: the service refuses a second decision
 *  until the first is reset, so the two flags cannot both be set on a line
 *  that went through the API. */
export function otifDecisionOf(line: Partial<OtifReviewable>): OtifDecision | undefined {
  if (line.otif_overridden) return 'overridden';
  if (line.otif_acknowledged) return 'acknowledged';
  return otifAwaitsDecision(line) ? 'not_decided' : undefined;
}

/**
 * CR-7418: the badge for a server-sent review STATUS string.
 *
 * The drill-downs and their exports get `otif_review` as one of three words
 * from `purchasing.services.otif_review.review_status`, so the badge is keyed
 * on the word rather than on two booleans the payload no longer carries. An
 * unrecognised or empty value prints a dash: a cell with nothing to say says
 * nothing, rather than guessing at "Not Decided".
 */
const REVIEW_STATUS_BADGE: Record<string, string> = {
  'Overridden': 'bg-teal-100 text-teal-800 border border-teal-300',
  'Acknowledged': 'bg-violet-100 text-violet-800 border border-violet-300',
  'Not Decided': 'bg-amber-100 text-amber-800 border border-amber-300',
};

export function OtifReviewStatusBadge({ value }: { value: unknown }) {
  const label = typeof value === 'string' ? value.trim() : '';
  const className = REVIEW_STATUS_BADGE[label];
  if (!className) return <span className="text-muted-foreground">-</span>;
  return <Badge className={className}>{label}</Badge>;
}

export const OTIF_DECISION_LABEL: Record<OtifDecision, string> = {
  overridden: 'Overridden',
  acknowledged: 'Acknowledged',
  not_decided: 'Not Decided',
};

/**
 * CR-7418 round 3 (Aaron, 19 Sep — he asked for the badge inventory, then left
 * the colours to me on the condition that every surface showing a decision
 * agrees: the column, the row buttons, the bulk bar and the modal header).
 *
 * One colour per MEANING, and the three collisions the inventory turned up are
 * what these values are chosen against:
 *
 *   teal = the fail was OVERRIDDEN. It went emerald -> red -> emerald -> TEAL
 *     across four looks at three screens, and the trail is worth keeping
 *     because every move was made against something on a screen. Emerald first
 *     (an override turns a fail into a pass, so: green). Red when Aaron saw the
 *     MODAL HEADER, where emerald sat beside the pale-green "100% Delivered"
 *     that an overridden line ALWAYS carries. Emerald again when he saw the
 *     OTIF drill, where he wanted it reading green against a red FAILED. Teal
 *     settles it (Aaron, 19 Sep): it is unmistakably in the green family, so it
 *     still says "this passes" beside a red FAILED, and it is not the same
 *     chip as 100% Delivered beside it in the header. One colour that works on
 *     BOTH surfaces, rather than the right colour for whichever screen was
 *     open last.
 *   violet = ACKNOWLEDGED — a decision WAS taken and the line still failed. It
 *     was pale blue, which is exactly the pale blue of "Waiting on Customer",
 *     and blue was already the solid New chip too: three meanings, one hue.
 *     Violet is unused in this row and reads as "reviewed, not resolved".
 *   amber = NOT DECIDED — work waiting on a person, which is what amber means
 *     everywhere else here (Exceptions, Changed). It was `destructive` red,
 *     which says something is WRONG; nothing is wrong with a line nobody has
 *     got to yet, and red already means Change Declined one column away.
 */
const DECISION_BADGE: Record<OtifDecision, string> = {
  overridden: 'bg-teal-100 text-teal-800 border border-teal-300',
  acknowledged: 'bg-violet-100 text-violet-800 border border-violet-300',
  not_decided: 'bg-amber-100 text-amber-800 border border-amber-300',
};

/** One badge, used by every surface that shows a decision, so a colour means
 *  the same thing in the grid, the modal and the review queue.
 *  `withPrefix` spells "OTIF …" for surfaces with no column header to say so. */
export function OtifDecisionBadge({
  line, withPrefix = false,
}: {
  line: Partial<OtifReviewable>;
  withPrefix?: boolean;
}) {
  const d = otifDecisionOf(line);
  // Nothing to review, nothing to say — the same dash `OtifReviewStatusBadge`
  // prints for a server value it cannot key on.
  if (!d) return <span className="text-muted-foreground">-</span>;
  return (
    <Badge className={DECISION_BADGE[d]}>
      {withPrefix ? `OTIF ${OTIF_DECISION_LABEL[d]}` : OTIF_DECISION_LABEL[d]}
    </Badge>
  );
}

/**
 * CR-7418: who decided, when, and why.
 *
 * A PO line records its OTIF decision as two booleans and nothing else — the
 * who/when/why lives only in the changelog row the service writes
 * (`po_line_workflow_service._create_changelog('otif_overridden', …)`). Note
 * the asymmetry, because it will surprise the next reader: the SCHEDULE-level
 * override has real `otif_overridden_by` / `otif_overridden_at` /
 * `otif_override_reason` columns; the LINE
 * level has none, so this reads the audit trail instead of a field.
 *
 * Latest entry wins: a line can be decided, reset and decided again, and it is
 * the current decision the modal is describing.
 */
export function otifDecisionAudit(
  changelogs: ChangelogEntry[],
  decision: OtifDecision | undefined,
): ChangelogEntry | null {
  if (!decision || decision === 'not_decided') return null;
  const field = decision === 'overridden' ? 'otif_overridden' : 'otif_acknowledged';
  const rows = changelogs
    .filter((c) => c.field_changed === field && c.new_value === 'True')
    .sort((a, b) => (a.change_date < b.change_date ? 1 : -1));
  return rows[0] || null;
}

/**
 * The reason a decision was taken, with the service's own restatement of the
 * decision stripped off the front.
 *
 * `po_line_workflow_service` writes the changelog reason as
 * `"OTIF overridden. <the user's words>"`. Shown next to a badge that already
 * reads "Overridden", that prefix is the sentence said twice — so the badge
 * carries the decision and this carries only what the person typed. Verified
 * against a live row on the local clone rather than read off the f-string.
 */
const DECISION_REASON_PREFIX = /^OTIF (overridden|acknowledged|decision reset)\.\s*/i;

export function otifDecisionReason(entry: { reason?: string } | null): string {
  if (!entry?.reason) return '';
  return entry.reason.replace(DECISION_REASON_PREFIX, '').trim();
}
